/**
 * First-party origin check for delegation-consent auto-approval.
 *
 * A delegation request whose *browser-verified* origin is first-party with the
 * wallet defaults its consent to `approve-always` and mints without a prompt
 * ("connecting the app was the explicit grant").
 *
 * IMPORTANT — the caller MUST pass the BROWSER-VERIFIED postMessage origin
 * (`MessageEvent.origin`, surfaced as the handler's `meta.origin`), NEVER the
 * self-declared `?origin=` query param or an `appContext.origin` value. The
 * verified origin is the only value an attacker cannot forge.
 *
 * ---
 *
 * THE TRUST BOUNDARY IS CONFIGURED, NOT GUESSED (CEF-AI/wallet#74).
 *
 * This previously approximated the registrable domain as the last two labels
 * of the wallet's own hostname and treated every subdomain of it as
 * first-party. That reasoning holds only for a two-label deployment such as
 * `wallet.example.com`. The deployed wallet host is five labels deep —
 * `wallet.compute.dev.ddcdragon.com` — so the approximation yielded
 * `ddcdragon.com`, and **anything served anywhere under that domain could mint
 * a delegation with no consent prompt.** Bounded in practice only by who
 * happened to own subdomains, which is not a boundary at all: it stops holding
 * the day the domain gains a shared, multi-tenant or user-content subdomain.
 *
 * The second-order cost was as bad as the first. Every origin anyone would
 * test with was first-party, so the prompt path was never reached and the
 * approvals store was never exercised — consent could not be meaningfully
 * tested on devnet or testnet at all, and any result there meant only "the
 * auto-approve branch was taken".
 *
 * So the guess is gone. The set of first-party origins is now explicit
 * per-environment configuration (`firstPartyOrigins` in the SPA's runtime
 * config). Entries are compared as whole origins after normalisation, so
 * `https://vault.example.com:443` and `https://vault.example.com` are the same
 * entry and a redundant port cannot silently fail to match.
 *
 * **It is NOT wallet-api's `WALLET_API_EMBED_TOP_ORIGINS`, though it is drawn
 * from the same population.** That list says who may RUN a ceremony; this one
 * says who SKIPS CONSENT, and the two answers differ for a cross-site
 * consumer: production prompts one, so auto-approving it in a dev environment
 * would measure a consent behaviour production does not have. Expect this list
 * to be the same size or smaller.
 *
 * FAILING CLOSED IS DELIBERATE. With no list configured, only the wallet's own
 * origin is first-party and every consumer sees a consent prompt. An
 * unconfigured deployment therefore over-prompts rather than under-prompts,
 * and the operator gets a warning naming the key. A silent auto-approve for
 * origins nobody enumerated is the failure this issue exists to remove; a
 * prompt too many is a visible, recoverable inconvenience.
 *
 * The PSL is still not used, and is still the right long-term answer for the
 * cross-site phase. It is a data dependency the repo avoids, and it is not
 * needed to state a list the deployment already knows.
 */
export function isFirstPartyOrigin(
  verifiedOrigin: string | undefined,
  walletOrigin: string | undefined,
  firstPartyOrigins?: readonly string[],
): boolean {
  if (!verifiedOrigin || !walletOrigin) return false;

  const candidate = parseOrigin(verifiedOrigin);
  const wallet = parseOrigin(walletOrigin);
  if (!candidate || !wallet) return false;

  // The wallet's own origin is always first-party and never needs configuring:
  // it is the document doing the asking.
  if (candidate === wallet) return true;

  if (!firstPartyOrigins || firstPartyOrigins.length === 0) {
    warnUnconfiguredOnce(verifiedOrigin);
    return false;
  }

  return firstPartyOrigins.some((entry) => parseOrigin(entry) === candidate);
}

/**
 * Normalise to a whole origin (scheme + host + port), dropping a default port
 * so `https://x:443` and `https://x` compare equal. Returns null for anything
 * unparseable, which is never first-party.
 */
function parseOrigin(value: string): string | null {
  try {
    const url = new URL(value);
    // `new URL('/foo')` throws, but `new URL('mailto:x')` does not and has an
    // opaque origin of "null" — never a match for anything.
    return url.origin === 'null' ? null : url.origin;
  } catch {
    return null;
  }
}

/**
 * Warn once per process. An unconfigured deployment is a deploy-time mistake,
 * not a per-request one, and repeating it for every delegation would bury it.
 */
let warnedUnconfigured = false;

function warnUnconfiguredOnce(refusedOrigin: string): void {
  if (warnedUnconfigured) return;
  warnedUnconfigured = true;
  console.warn(
    `[wallet] no first-party origins are configured, so "${refusedOrigin}" will be prompted for ` +
      `consent rather than auto-approved. Set \`firstPartyOrigins\` in the deployed config.js ` +
      `(window.__RUNTIME_CONFIG__), or VITE_WALLET_FIRST_PARTY_ORIGINS for local dev. It is the ` +
      `list of origins that SKIP CONSENT — drawn from the same population as wallet-api's ` +
      `WALLET_API_EMBED_TOP_ORIGINS (who may RUN a ceremony) but not the same list, and usually ` +
      `smaller: a cross-site consumer belongs in that one and not this one, because production ` +
      `prompts it. See CEF-AI/wallet#74.`,
  );
}

/** Test seam: the warning is once-per-process, so suites must be able to reset it. */
export function resetFirstPartyWarningForTests(): void {
  warnedUnconfigured = false;
}
